import React, { useState, useMemo, useEffect } from "react";
import { Job, JobFilters as JobFiltersType } from "../types";
import {
  loadJobsFromJson,
  getJobStatsFromJson,
  getJobsFileDateFromJson,
  getUniqueCompaniesFromJson,
  getUniqueLocationsFromJson,
  getAllUniqueTagsFromJson,
} from "../data/jobs-loader";
import JobFilters from "./JobFilters";
import JobCard from "./JobCard";
import LiquidGlassContainer from "./LiquidGlassContainer";
import { BriefcaseIcon } from "../utils/icons";
import { refreshAllLiquidGlass } from "../utils/loadLiquidGlass";
import "../styles/JobBoard.css";

const JobBoardContent: React.FC = () => {
  const [filters, setFilters] = useState<JobFiltersType>({
    search: "",
    location: "",
    company: "",
    jobType: "",
    remote: false,
    tags: [],
    salaryDisclosed: false,
  });
  const [allJobs, setAllJobs] = useState<Job[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [jobStats, setJobStats] = useState<any>(null);
  const [jobsFileDate, setJobsFileDate] = useState<string | null>(null);
  const [filterOptions, setFilterOptions] = useState({
    companies: [] as string[],
    locations: [] as string[],
    tags: [] as string[],
  });
  const [error, setError] = useState<string | null>(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const jobsPerPage = 10;

  // Load all jobs and filter options on component mount
  useEffect(() => {
    const loadJobsAndFilters = async () => {
      try {
        console.log("Loading jobs and filter options...");

        // Load jobs from jobs.json
        const jobs = await loadJobsFromJson();
        console.log("Jobs loaded from jobs.json:", jobs.length, "jobs");
        setAllJobs(jobs);

        // Load filter options
        const [companies, locations, tags] = await Promise.all([
          getUniqueCompaniesFromJson(),
          getUniqueLocationsFromJson(),
          getAllUniqueTagsFromJson(),
        ]);

        setFilterOptions({
          companies,
          locations,
          tags,
        });

        // Get job statistics
        const stats = await getJobStatsFromJson();
        const date = await getJobsFileDateFromJson();
        console.log("Job stats:", stats);
        setJobStats(stats);
        setJobsFileDate(date);
      } catch (error) {
        console.error("Error loading jobs:", error);
        setError(
          "Failed to load job data from jobs.json. Please try refreshing the page.",
        );
        // Set empty state on error
        setAllJobs([]);
        setJobStats(null);
      } finally {
        setIsLoading(false);
      }
    };

    loadJobsAndFilters();
  }, []);

  console.log(allJobs);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filters]);

  // Filter jobs based on current filters
  const filteredJobs = useMemo(() => {
    const filtered = allJobs.filter((job) => {
      // Search filter
      if (filters.search) {
        const searchLower = filters.search.toLowerCase();
        const matchesSearch =
          job.title.toLowerCase().includes(searchLower) ||
          job.company.toLowerCase().includes(searchLower) ||
          job.description.toLowerCase().includes(searchLower) ||
          job.tags.some((tag) => tag.toLowerCase().includes(searchLower));

        if (!matchesSearch) return false;
      }

      // Location filter
      if (filters.location && filters.location !== "All Locations") {
        if (!job.location || job.location.length === 0) return false;

        // Check if filter is for a remote position by country
        if (filters.location.startsWith("Remote (")) {
          // Extract country from filter (e.g., "Remote (United States)" -> "United States")
          const filterCountry = filters.location.match(/Remote \((.+)\)/)?.[1];
          if (!filterCountry) return false;

          // Check if job is remote and has matching country
          if (!job.remote) return false;
          const hasMatchingRemoteCountry = job.location.some((loc) => {
            const parts = loc.split(", ");
            const country = parts[parts.length - 1];
            return country === filterCountry;
          });
          if (!hasMatchingRemoteCountry) return false;
        } else {
          // Check if any location in the job's location array contains the filter text
          const hasMatchingLocation = job.location.some((loc) =>
            loc.toLowerCase().includes(filters.location.toLowerCase()),
          );
          if (!hasMatchingLocation) return false;
        }
      }

      // Company filter
      if (filters.company && job.company !== filters.company) {
        return false;
      }

      // Job type filter
      if (filters.jobType && filters.jobType !== "all") {
        if (job.type !== filters.jobType) return false;
      }

      // Remote filter
      if (filters.remote && !job.remote) return false;

      // Salary filter
      if (filters.salaryDisclosed) {
        if (!job.salary) return false;
      }

      // Tags filter
      if (filters.tags.length > 0) {
        const hasMatchingTag = filters.tags.some((tag) =>
          job.tags.some((jobTag) =>
            jobTag.toLowerCase().includes(tag.toLowerCase()),
          ),
        );
        if (!hasMatchingTag) return false;
      }

      return true;
    });

    // Sort jobs by date (newest first) and then by job ID for stable ordering
    return filtered.sort((a, b) => {
      // First, sort by date (newest first)
      const dateA = new Date(a.date);
      console.log(a.date);
      const dateB = new Date(b.date);
      if (dateA > dateB) return -1;
      if (dateA < dateB) return 1;
      // If dates are the same, sort by job ID for consistent ordering
      return a.contentHash.localeCompare(b.contentHash);
    });
  }, [allJobs, filters]);

  useEffect(() => {
    if (!isLoading) {
      refreshAllLiquidGlass();
    }
  }, [isLoading, jobStats, filteredJobs.length, currentPage]);

  // Pagination calculations
  const totalPages = Math.ceil(filteredJobs.length / jobsPerPage);
  const startIndex = (currentPage - 1) * jobsPerPage;
  const endIndex = startIndex + jobsPerPage;
  const currentJobs = filteredJobs.slice(startIndex, endIndex);

  // Navigation functions
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleApply = (job: Job) => {
    // Open job application URL in new tab
    window.open(job.link, "_blank");
  };

  const handleRefresh = () => {
    setIsLoading(true);
    // Clear cache and reload
    window.location.reload();
  };

  return (
    <section className="max-w-haig-container mx-auto py-16 px-4 md:px-6">
      {/* Header */}
      <div className="header-container text-center mb-5">
        <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
          <img
            src="assets/logos/healthcare_ai_guy.jpg"
            className="haig-logo"
            alt="Healthcare AI Guy logo"
          />
          <h1 className="text-3xl sm:text-4xl font-bold text-haig-primary tracking-tight text-center">
            Healthcare AI Job Board
          </h1>
        </div>
        <p className="text-lg sm:text-xl text-haig-muted max-w-3xl mx-auto leading-relaxed">
          Discover the latest opportunities in AI-powered healthcare. Find your
          next role in this rapidly growing field.
        </p>

        {/* Job Statistics */}
        {jobStats && (
          <div>
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
              <LiquidGlassContainer
                borderRadius={8}
                tintOpacity={0.22}
                className="job-glass-stat"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-haig-secondary">
                    {jobStats.totalJobs}
                  </div>
                  <div className="text-sm text-haig-muted">Total Jobs</div>
                </div>
              </LiquidGlassContainer>
              <LiquidGlassContainer
                borderRadius={8}
                tintOpacity={0.22}
                className="job-glass-stat"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-haig-primary">
                    {jobStats.remoteCount}
                  </div>
                  <div className="text-sm text-haig-muted">Remote Positions</div>
                </div>
              </LiquidGlassContainer>
              <LiquidGlassContainer
                borderRadius={8}
                tintOpacity={0.22}
                className="job-glass-stat"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-haig-secondary">
                    {Object.keys(jobStats.companyCounts).length}
                  </div>
                  <div className="text-sm text-haig-muted">Companies</div>
                </div>
              </LiquidGlassContainer>
              <LiquidGlassContainer
                borderRadius={8}
                tintOpacity={0.22}
                className="job-glass-stat"
              >
                <div className="text-center">
                  <div className="text-2xl font-bold text-haig-primary">
                    {jobStats.uniqueTags.length}
                  </div>
                  <div className="text-sm text-haig-muted">Skill Areas</div>
                </div>
              </LiquidGlassContainer>
            </div>
            <p className="text-base text-haig-muted max-w-3xl mx-auto mt-8">
              Interested in seeing your jobs here? Fill out this
              <a
                href="https://airtable.com/appOTF8yOIwLbqr7F/pagfl4ttxs0O2hOeX/form?utm_source=www.healthcareaiguy.com&utm_medium=newsletter&utm_campaign=healthcare-ai-guy-weekly-10-21&_bhlid=c1d496586ccc9d2c8fb2e4d84952994cc16d9983"
                target="_blank"
                rel="noreferrer"
              >
                {" "}
                <u className="text-haig-secondary hover:opacity-80">form</u>
              </a>
              .
            </p>
          </div>
        )}

        {/* Last Updated */}
        {jobsFileDate && (
          <div className="mt-4 flex items-center justify-center space-x-4 text-sm text-haig-muted">
            <span>
              Last updated:{" "}
              {new Date(jobsFileDate).toLocaleDateString()}
            </span>
            <button
              onClick={handleRefresh}
              className="text-haig-secondary hover:opacity-80 underline font-semibold transition-all"
              title="Refresh job data"
            >
              Refresh
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <JobFilters
        filters={filters}
        onFiltersChange={setFilters}
        totalJobs={filteredJobs.length}
        filterOptions={filterOptions}
      />

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-haig p-4 mb-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-800">{error}</p>
            </div>
            <div className="ml-auto pl-3">
              <button
                onClick={handleRefresh}
                className="text-sm text-red-600 hover:text-red-800 underline"
              >
                Try again
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Listings */}
      <div className="space-y-6">
        {isLoading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-2 border-haig-border border-t-haig-secondary mx-auto mb-4"></div>
            <p className="text-haig-muted">Loading jobs...</p>
          </div>
        ) : filteredJobs.length > 0 ? (
          <>
            <div className="mb-6 text-sm text-haig-muted">
              Showing {startIndex + 1}-{Math.min(endIndex, filteredJobs.length)}{" "}
              of {filteredJobs.length} jobs
              {filteredJobs.length > allJobs.length &&
                ` (filtered from ${allJobs.length} total)`}
            </div>

            {/* Current Page Jobs */}
            {currentJobs.map((job) => (
              <JobCard key={job.id} job={job} onApply={handleApply} />
            ))}

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center space-x-2 mt-8">
                {/* Previous Page Button */}
                <button
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                  className={`px-3 py-2 rounded-haig text-sm font-semibold transition-all duration-200 ${
                    currentPage === 1
                      ? "bg-haig-surface text-haig-muted cursor-not-allowed border border-haig-border"
                      : "bg-haig-background text-haig-text hover:bg-haig-surface border border-haig-border"
                  }`}
                >
                  Previous
                </button>

                {/* Page Numbers */}
                <div className="flex items-center space-x-1">
                  {/* First Page */}
                  {currentPage > 3 && (
                    <>
                      <button
                        onClick={() => goToPage(1)}
                        className="px-3 py-2 rounded-haig text-sm font-semibold bg-haig-background text-haig-text hover:bg-haig-surface border border-haig-border transition-all duration-200"
                      >
                        1
                      </button>
                      {currentPage > 4 && (
                        <span className="px-2 text-haig-muted">...</span>
                      )}
                    </>
                  )}

                  {/* Page Numbers Around Current */}
                  {(() => {
                    const pages = [];
                    const startPage = Math.max(1, currentPage - 2);
                    const endPage = Math.min(totalPages, currentPage + 2);

                    for (let i = startPage; i <= endPage; i++) {
                      // Skip if this page would duplicate first or last page buttons
                      if (
                        (i === 1 && currentPage > 3) ||
                        (i === totalPages && currentPage < totalPages - 2)
                      ) {
                        continue;
                      }

                      pages.push(
                        <button
                          key={i}
                          onClick={() => goToPage(i)}
                          className={`px-3 py-2 rounded-haig text-sm font-semibold transition-all duration-200 ${
                            i === currentPage
                              ? "bg-haig-secondary text-white"
                              : "bg-haig-background text-haig-text hover:bg-haig-surface border border-haig-border"
                          }`}
                        >
                          {i}
                        </button>,
                      );
                    }
                    return pages;
                  })()}

                  {/* Last Page */}
                  {currentPage < totalPages - 2 && (
                    <>
                      {currentPage < totalPages - 3 && (
                        <span className="px-2 text-haig-muted">...</span>
                      )}
                      <button
                        onClick={() => goToPage(totalPages)}
                        className="px-3 py-2 rounded-haig text-sm font-semibold bg-haig-background text-haig-text hover:bg-haig-surface border border-haig-border transition-all duration-200"
                      >
                        {totalPages}
                      </button>
                    </>
                  )}
                </div>

                {/* Next Page Button */}
                <button
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-2 rounded-haig text-sm font-semibold transition-all duration-200 ${
                    currentPage === totalPages
                      ? "bg-haig-surface text-haig-muted cursor-not-allowed border border-haig-border"
                      : "bg-haig-background text-haig-text hover:bg-haig-surface border border-haig-border"
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="text-center py-12">
            <BriefcaseIcon className="h-16 w-16 text-haig-muted mx-auto mb-4 opacity-60" />
            <h3 className="text-xl font-bold text-haig-primary mb-2">
              {allJobs.length === 0 ? "No jobs available" : "No jobs found"}
            </h3>
            <p className="text-haig-muted">
              {allJobs.length === 0
                ? "Job data is currently being updated. Please check back soon."
                : "Try adjusting your filters or search terms to find more opportunities."}
            </p>
            {allJobs.length > 0 && (
              <button
                onClick={() =>
                  setFilters({
                    search: "",
                    location: "",
                    company: "",
                    jobType: "",
                    remote: false,
                    tags: [],
                    salaryDisclosed: false,
                  })
                }
                className="mt-4 px-5 py-2.5 bg-haig-secondary text-white rounded-haig font-semibold hover:brightness-95 transition-all duration-200"
              >
                Clear all filters
              </button>
            )}
          </div>
        )}
      </div>

      {/* Footer Info */}
      <div className="mt-16 text-center text-haig-muted">
        <p className="text-sm">
          Jobs are automatically scraped and updated from leading healthcare AI
          companies using our Python scraper system.
        </p>
        <p className="text-sm mt-2">
          Have a job to post? Fill out this
          <a
            href="https://airtable.com/appOTF8yOIwLbqr7F/pagfl4ttxs0O2hOeX/form?utm_source=www.healthcareaiguy.com&utm_medium=newsletter&utm_campaign=healthcare-ai-guy-weekly-10-21&_bhlid=c1d496586ccc9d2c8fb2e4d84952994cc16d9983"
            target="_blank"
            rel="noreferrer"
          >
            {" "}
            <u className="text-haig-secondary hover:opacity-80">form</u>{" "}
          </a>
          to list your opportunity.
        </p>
      </div>
    </section>
  );
};

export default JobBoardContent;
